import React, { useState, useMemo, Fragment } from "react";
import { FormattedMessage } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import { loginSetFlowStageWatcher, sendTotpCode } from "../../redux/actions/loginactioncreators";
import { QRCodeSVG } from "qrcode.react";
import * as OTPAuth from "otpauth";
import { MfaPageContainer } from "./MfaPageContainer";
import OtpCodeEntry from "./MfaOtpCodeEntry";
import flowStages from "../../flowStages";
import { SmallScreenExpand } from "./SmallScreenExpand";

const MfaTotp = () => {
  const [totpCode, setTotpCode] = useState("");
  const [totpCodeValid, setTotpCodeValid] = useState(false);

  const dispatch = useDispatch();

  const {
    username,
    spinning,
    secretCode,
    mfaTotpConfig,
    complete
  } = useSelector(state => ({
    username: state.loginForm.username,
    spinning: state.loginForm.totpSpinning,
    secretCode: state.loginForm.secretCode,
    mfaTotpConfig: state.loginForm.mfa.config.totp,
    complete: state.loginForm.complete
  }));

  const otpCodeLength = mfaTotpConfig?.codeLength;

  const otpOnChange = (value) => {
    setTotpCode(value);
    setTotpCodeValid(value.length === otpCodeLength);
  };

  const isTotpCodeValid = (value) => {
    return value.length === otpCodeLength;
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    if (totpCodeValid) {
      dispatch(sendTotpCode(totpCode));
    }
  }

  const handleClickBack = () => {
    dispatch(loginSetFlowStageWatcher({ flowStage: flowStages.MFA_SETUP }));
  }

  const splitLongStringEvery = (string, separator, length) => {
    return string?.split("").reduce((a, e, i) => a + e + (i % length === length - 1 ? separator : ""), "");
  };

  const FormattedTotpSecretCode = () => {
    return (
      <div id="totpCode" className="font-bold">
        {splitLongStringEvery(secretCode, " ", 4)}
      </div>
    );
  };

  const qrCode = useMemo(() => {
    if (!secretCode) return undefined;

    return new OTPAuth.TOTP({
      issuer: "First Advantage",
      label: username,
      algorithm: "SHA1",
      digits: 6,
      period: 30,
      secret: secretCode,
    });
  }, [secretCode, username]);


  const InstructionalText = () => {
    return (
      <SmallScreenExpand id="totpInstructionalTextExpand">
        <FormattedMessage
          tagName="div"
          id="mfaSetup.totpSetupBlurb"
          defaultMessage="Use a third-party authenticator app on your mobile device to verify your identity. The links below are for reference purposes and should be accessed from your mobile device to download your preferred app:"
          description="Instructional text explaining how to use TOTP authenticator apps"
        />
        <a className="text-blue-700" href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2" target="_blank" rel="noopener noreferrer">
          <FormattedMessage
            tagName="div"
            id="mfaSetup.googleAuthenticatorAndroid"
            defaultMessage="Google Authenticator for Android"
            description="Link text for Google Authenticator app for Android"
          />
        </a>
        <a className="text-blue-700" href="https://apps.apple.com/us/app/google-authenticator/id388497605" target="_blank" rel="noopener noreferrer">
          <FormattedMessage
            tagName="div"
            id="mfaSetup.googleAuthenticatorIphone"
            defaultMessage="Google Authenticator App for iPhone"
            description="Link text for Google Authenticator app for iPhone"
          />
        </a>
        <a className="text-blue-700" href="https://authy.com/" target="_blank" rel="noopener noreferrer">
          <FormattedMessage
            tagName="div"
            id="mfaSetup.authy"
            defaultMessage="Authy"
            description="Link text for Authy authenticator app"
          />
        </a>
        <a className="text-blue-700" href="https://www.microsoft.com/en-us/security/mobile-authenticator-app" target="_blank" rel="noopener noreferrer">
          <FormattedMessage
            tagName="div"
            id="mfaSetup.msAuthenticator"
            defaultMessage="Microsoft Authenticator"
            description="Link text for Microsoft Authenticator app"
          />
        </a>
        <a className="text-blue-700" href="https://support.apple.com/guide/iphone/automatically-fill-in-verification-codes-ipha6173c19f/ios" target="_blank" rel="noopener noreferrer">
          <FormattedMessage
            tagName="div"
            id="mfaSetup.appleAuthenticator"
            defaultMessage="Apple Authenticator"
            description="Link text for Apple Authenticator"
          />
        </a>
        <FormattedMessage
          tagName="div"
          id="mfaSetup.totpScanInstruction"
          defaultMessage="After installing the application, scan the QR code below or enter the code manually"
          description="Instructions for scanning QR code or entering code manually" />
      </SmallScreenExpand>
    );
  };

  const QRCodeSetUp = () => {
    return (
      <Fragment>
        <QRCodeSVG value={OTPAuth.URI.stringify(qrCode)} />
        <input type="hidden" id="totpUri" value={qrCode} />
        <FormattedMessage
          tagName="div"
          id="mfaSetup.totpSetupScanBlurb"
          defaultMessage="Scan the barcode above into your App, then enter the {codeLength} digit value generated before the time runs out."
          values={{ codeLength: otpCodeLength }}
          description="Instructions for scanning barcode and entering the generated code" />
        <FormattedMessage
          tagName="div"
          id="mfaSetup.totpSetupSecretCodeLabel"
          defaultMessage="Can't scan the barcode? Enter the key provided below manually into your App to generate the {codeLength} digit value."
          values={{ codeLength: otpCodeLength }}
          description="Alternative instructions for manually entering the secret key" />
        <FormattedTotpSecretCode />
      </Fragment>
    );
  };

  return (
    <div id="totpSetup">
      {!spinning && !complete && qrCode && (
        <MfaPageContainer
          onSubmit={handleSubmit}
          showErrors={true}
          requiredFields={[]}
          headerText={<FormattedMessage id="mfaSetup.totpSetupHeader" defaultMessage="Use an Authenticator App" description="Header text for TOTP MFA setup page" />}
          instructionalText={<InstructionalText />}
          submitButtonText={<FormattedMessage id="mfaSetup.continueButton" defaultMessage="Continue" description="Submit multi-factor authentication selection" />}
          secondaryButtonText={<FormattedMessage id="mfaSetup.backButton" defaultMessage="Back" description="Return to login" />}
          secondaryButtonHandler={handleClickBack}
          onChange={otpOnChange}
          otpValidator={isTotpCodeValid}
        >
          <QRCodeSetUp id="qrCodeSetUp" />
          <OtpCodeEntry id="otpCodeEntry" length={otpCodeLength} onChange={otpOnChange} />
        </MfaPageContainer>
      )}
    </div>
  );
};

export default MfaTotp;
